import { useMutation, MutationResult, DataProxy } from '@apollo/client';

import { useCallback } from 'react';
import { ExecutionResult } from 'graphql';
import getStravaSettingsQuery from '../../components/StravaSettingsModals/StravaSettingsModal/stravaSettings.gql';
import {
  FitnessApplicationSettingsInput,
  SetFitnessApplicationSettingsMutation,
  SetFitnessApplicationSettingsMutationVariables,
  GetStravaSettingsQuery,
  GetStravaSettingsQueryVariables,
  PageType,
  Page,
  FitnessApplication,
} from '../../../types/__generated__';
import { PageMetaContext } from '../../context/PageMetaContext';
import { useNotNullableContext } from '../useNotNullableContext';
import mutation from './queries/setFitnessSettings.gql';
import listTimelineEntriesQuery from '../timeline/queries/listTimelineEntries.gql';

type UpdateStravaSettings = (
  input: FitnessApplicationSettingsInput,
) => Promise<ExecutionResult<SetFitnessApplicationSettingsMutation>>;

export default function useSaveStravaSettings(): [
  UpdateStravaSettings,
  MutationResult<SetFitnessApplicationSettingsMutation>,
] {
  const { pageType, pageSlug } = useNotNullableContext(PageMetaContext);

  const [runMutation, mutationResult] = useMutation<
    SetFitnessApplicationSettingsMutation,
    SetFitnessApplicationSettingsMutationVariables
  >(mutation, {
    update(cache, { data }) {
      if (!data || !data.setFitnessApplicationSettings) {
        return;
      }

      const result = readStravaSettings(pageType, pageSlug, cache);

      const { previousPage, previousViewer } = result;

      const stravaSettings: FitnessApplication = {
        __typename: 'FitnessApplication',
        settings: data.setFitnessApplicationSettings,
      };

      const newData = {
        viewer: previousViewer,
        page: {
          ...previousPage,
          strava: stravaSettings,
        },
      };

      writeStravaSettings(pageType, pageSlug, newData, cache);
    },
    refetchQueries: (
      result: MutationResult<SetFitnessApplicationSettingsMutation>,
    ) => {
      return [
        {
          query: listTimelineEntriesQuery,
          variables: {
            type: pageType.toUpperCase() as PageType,
            slug: pageSlug,
            measurementSystem:
              result.data?.setFitnessApplicationSettings?.measurementSystem,
          },
          context: {
            credentials: 'include',
          },
        },
      ];
    },
    context: {
      credentials: 'include',
    },
  });

  return [
    useCallback(
      input =>
        runMutation({
          variables: {
            input,
          },
        }),
      [runMutation],
    ),
    mutationResult,
  ];
}

const readStravaSettings = (
  pageType: string,
  pageSlug: string,
  cache: DataProxy,
) => {
  const result = cache.readQuery<
    GetStravaSettingsQuery,
    GetStravaSettingsQueryVariables
  >({
    query: getStravaSettingsQuery,
    variables: {
      type: pageType.toUpperCase() as PageType,
      slug: pageSlug,
    },
  });

  const previousViewer = result?.viewer;
  const previousPage =
    result?.page ??
    ({
      id: '',
      legacyId: '',
      type: pageType.toUpperCase() as PageType,
      slug: pageSlug,
      url: '',
      title: '',
    } as Page);

  return { previousViewer, previousPage };
};

const writeStravaSettings = (
  pageType: string,
  pageSlug: string,
  data: GetStravaSettingsQuery,
  cache: DataProxy,
): void => {
  cache.writeQuery<GetStravaSettingsQuery, GetStravaSettingsQueryVariables>({
    query: getStravaSettingsQuery,
    data,
    variables: {
      type: pageType.toUpperCase() as PageType,
      slug: pageSlug,
    },
  });
};
